'use client';

import { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import StepIndicator from './StepIndicator';
import Step1UserAndAccountType from './steps/Step1UserAndAccountType';
import Step3AccountDetails from './steps/Step3AccountDetails';
import BuyerStep4PersonalDetails from './steps/buyer/BuyerStep4PersonalDetails';
import BuyerStep5Documents from './steps/buyer/BuyerStep5Documents';
import BuyerStep6BusinessDetails from './steps/buyer/BuyerStep6BusinessDetails';
import SellerStep4AdditionalDetails from './steps/seller/SellerStep4AdditionalDetails';
import SellerStep5Documents from './steps/seller/SellerStep5Documents';
import SellerStep6BankDetails from './steps/seller/SellerStep6BankDetails';
import SellerStep7BusinessDetails from './steps/seller/SellerStep7BusinessDetails';
import StepNavigation from './StepNavigation';
import bgimg from "../../../assets/loginSignupBg.png";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { registerBuyer, registerSeller } from '../../../services/authService.ts';
import { useLanguage } from '../../../context/LanguageContext';
import api from '../../../lib/api.ts';

const SignupForm = () => {
  const { isRtl, translate, language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(0); // -1 for previous, 1 for next
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const isRTL = language === "ar";
  const [userType, setUserType] = useState(null); // 'buyer' or 'seller'


  const buyerSchema = (translate, currentStep) => z.object({
    userType: z.enum(['buyer', 'seller'], {
      required_error: translate('auth.signup.validation.userTypeRequired'),
    }),
    accountType: z.enum(['corporate', 'individual'], {
      required_error: translate('auth.signup.validation.accountTypeRequired'),
    }),

    fullName: z.string().trim().min(1, translate('auth.signup.validation.fullNameRequired')),
    phoneNumber: z.string().min(7, translate('auth.signup.validation.phoneRequired')),
    email: z.string().email(translate('auth.signup.validation.invalidEmail')),
    password: z
      .string()
      .min(8, translate('auth.signup.validation.passwordMin'))
      .regex(/[A-Z]/, translate('auth.signup.validation.passwordUppercase'))
      .regex(/[a-z]/, translate('auth.signup.validation.passwordLowercase'))
      .regex(/[^A-Za-z0-9]/, translate('auth.signup.validation.passwordSpecial')),


    location: z.string().trim().min(1, translate('auth.signup.validation.locationRequired')),
    emiratesIdNumber: z
      .string()
      .min(1, translate('auth.signup.validation.emiratesIdNumberRequired'))
      .regex(/^[a-zA-Z0-9-]+$/, translate('auth.signup.validation.emiratesIdPattern')),
    eidExpiryDate: z
      .string()
      .min(1, translate('auth.signup.validation.eidExpiryRequired'))
      .refine((date) => {
        if (!date) return false;
        const expiryDate = new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return expiryDate >= today;
      }, translate('auth.signup.validation.expiryDatePast')),

    idFront: z
      .any()
      .refine((files) => files?.length >= 1, translate('auth.signup.validation.idFrontRequired')),
    idBack: z.any().optional(),

    // Business Details (Optional by default, required for corporate via superRefine)
    businessName: z.string().optional(),
    businessRegistrationNumber: z.string().optional(),
    vatNumber: z.string().optional(),
    tradeLicenseImage: z.any().optional(),
    trnCertificationImage: z.any().optional(),
  }).superRefine((data, ctx) => {
    if (data.accountType === 'corporate' && currentStep >= 5) {
      if (!data.businessName || data.businessName.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translate('auth.signup.validation.businessNameRequired'),
          path: ['businessName'],
        });
      }
      if (!data.businessRegistrationNumber || data.businessRegistrationNumber.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translate('auth.signup.validation.businessRegNoRequired'),
          path: ['businessRegistrationNumber'],
        });
      }
      /* 
            if (!data.vatNumber || data.vatNumber.trim() === '') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: translate('auth.signup.validation.vatNumberRequired'),
                path: ['vatNumber'],
              });
            }
      */
      /* 
      if (!data.tradeLicenseImage || data.tradeLicenseImage.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translate('auth.signup.validation.tradeLicenseRequired'),
          path: ['tradeLicenseImage'],
        });
      }
      if (!data.trnCertificationImage || data.trnCertificationImage.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translate('auth.signup.validation.trnCertificateRequired'),
          path: ['trnCertificationImage'],
        });
      }
      */
    }
  });

  const sellerSchema = (translate, currentStep) => z.object({
    // STEP 1
    userType: z.enum(['buyer', 'seller'], {
      required_error: translate('auth.signup.validation.userTypeRequired'),
    }),

    // STEP 2
    accountType: z.enum(['corporate', 'individual'], {
      required_error: translate('auth.signup.validation.accountTypeRequired'),
    }),

    // STEP 3 – Account Details
    fullName: z.string().trim().min(1, translate('auth.signup.validation.fullNameRequired')),
    phoneNumber: z
      .string()
      .min(7, translate('auth.signup.validation.phoneRequired'))
      .regex(/^[0-9+\-\s]+$/, translate('auth.signup.validation.invalidPhone')),
    email: z.string().email(translate('auth.signup.validation.invalidEmail')),
    password: z
      .string()
      .min(8, translate('auth.signup.validation.passwordMin'))
      .regex(/[A-Z]/, translate('auth.signup.validation.passwordUppercase'))
      .regex(/[a-z]/, translate('auth.signup.validation.passwordLowercase'))
      .regex(/[^A-Za-z0-9]/, translate('auth.signup.validation.passwordSpecial')),


    // STEP 4 – Seller Additional Details
    location: z.string().trim().min(1, translate('auth.signup.validation.locationRequired')),
    vatNumber: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine((val) => !val || /^[0-9]+$/.test(val), translate('auth.signup.validation.vatNumberNumbersOnly')),
    emiratesIdNumber: z
      .string()
      .min(1, translate('auth.signup.validation.emiratesIdNumberRequired'))
      .regex(/^[a-zA-Z0-9-]+$/, translate('auth.signup.validation.emiratesIdPattern')),
    idExpiryDate: z
      .string()
      .min(1, translate('auth.signup.validation.idExpiryRequired'))
      .refine((date) => {
        if (!date) return false;
        const expiryDate = new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return expiryDate >= today;
      }, translate('auth.signup.validation.expiryDatePast')),

    //STEP 5 – Documents
    idFront: z
      .any()
      .refine((files) => files?.length >= 1, translate('auth.signup.validation.idFrontRequired')),
    idBack: z.any().optional(),



    // STEP 6 – Bank Details
    bankName: z.string().trim().optional().or(z.literal('')),
    accountHolderName: z.string().trim().optional().or(z.literal('')),
    accountNumber: z.string().trim().optional().or(z.literal('')),
    iban: z.string().trim().optional().or(z.literal('')),

    // STEP 7 – Business Details
    businessName: z.string().optional(),
    businessRegistrationNumber: z.string().optional(),

    tradeLicenseImage: z.any().optional(),
    trnCertificationImage: z.any().optional(),
  }).superRefine((data, ctx) => {
    // If on Step 6 (Business Details), certain fields are required
    if (currentStep >= 6) {
      // businessName and businessRegistrationNumber only for corporate
      if (data.accountType !== 'individual') {
        if (!data.businessName || data.businessName.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: translate('auth.signup.validation.businessNameRequired'),
            path: ['businessName'],
          });
        }
        if (!data.businessRegistrationNumber || data.businessRegistrationNumber.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: translate('auth.signup.validation.businessRegNoRequired'),
            path: ['businessRegistrationNumber'],
          });
        }
      }

      // Documentation optional for Sellers as requested
      /* 
            if (!data.tradeLicenseImage || data.tradeLicenseImage.length === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: translate('auth.signup.validation.tradeLicenseRequired'),
                path: ['tradeLicenseImage'],
              });
            }
            if (!data.trnCertificationImage || data.trnCertificationImage.length === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: translate('auth.signup.validation.trnCertificateRequired'),
                path: ['trnCertificationImage'],
              });
            }
      */
    }
  });

  const methods = useForm({
    mode: 'all',
    reValidateMode: 'all',
    resolver: async (values, context, options) => {
      const schema = values.userType === 'seller'
        ? sellerSchema(translate, currentStep)
        : buyerSchema(translate, currentStep);
      return zodResolver(schema)(values, context, options);
    },
    defaultValues: {
      userType: '',
      accountType: '',
      fullName: '',
      phoneNumber: '',
      email: '',
      password: '',
      location: '',
      emiratesIdNumber: '',
      eidExpiryDate: '',
      vatNumber: '',
      idExpiryDate: '',
      bankName: '',
      accountHolderName: '',
      accountNumber: '',
      iban: '',
      businessName: '',
      businessRegistrationNumber: '',
    },
  });

  // Clear errors and reset submitted state whenever the step changes to ensure a clean state on page mount
  useEffect(() => {
    // Reset the form state for the new step
    methods.reset(methods.getValues(), {
      keepValues: true,       // Maintain user input
      keepDefaultValues: true,
      keepErrors: false,       // Clear errors
      keepIsSubmitted: false,  // Reset submission status
      keepTouched: false,      // Reset touched fields
    });
  }, [currentStep, methods]);

  const formUserType = methods.watch('userType');
  const formAccountType = methods.watch('accountType');
  const maxSteps = formUserType === 'buyer'
    ? (formAccountType === 'corporate' ? 5 : 4)
    : 6;

  const handleUserTypeSelect = (type) => {
    setUserType(type);
    methods.setValue('userType', type, { shouldValidate: true });
    methods.setValue('accountType', '');
    // Removed immediate jump to step 2 to allow accountType selection on the same page
  };

  const handleNext = async (e) => {
    // Prevent default if an event is passed (though StepNavigation uses type="button")
    if (e && e.preventDefault) e.preventDefault();

    // If we are already loading, don't allow another next/submit
    if (isLoading) return;

    if (currentStep === maxSteps) {
      console.log(`[SignupForm] handleNext - Final step (${currentStep}) reached. Triggering handleSubmit.`);
      // Explicitly trigger form submission only on the last step
      await methods.handleSubmit((data, event) => onSubmit(data, event))();
      return;
    }

    const fieldsToValidate = getFieldsForStep(currentStep);
    console.log(`[SignupForm] handleNext - Step: ${currentStep}, MaxSteps: ${maxSteps}, Validating:`, fieldsToValidate);
    const isValid = await methods.trigger(fieldsToValidate);
    console.log(`[SignupForm] handleNext - isValid: ${isValid}`);

    if (!isValid) {
      // Force "touching" only fields that have errors for the current step
      const currentErrors = methods.formState.errors;
      fieldsToValidate.forEach(field => {
        if (currentErrors[field]) {
          methods.setValue(field, methods.getValues(field), { shouldTouch: true });
        }
      });
      // Also manually trigger validation again to ensure errors are populated after touching
      methods.trigger(fieldsToValidate);

      const firstErrorField = fieldsToValidate.find(field => methods.formState.errors[field]);
      if (firstErrorField) {
        setTimeout(() => methods.setFocus(firstErrorField), 100);
      }
      return;
    }

    // Uniqueness check for email and phone on step 2
    if (currentStep === 2) {
      setIsLoading(true);
      try {
        const values = methods.getValues();
        const response = await api.post(`/auth/checkunique`, {
          email: values.email,
          mobileNo: values.phoneNumber,
        });

        if (response.data && response.data.error) {
          showError(response.data.message || "Email or phone number already registered");
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error("Error checking uniqueness:", error);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || "Email or phone number already registered";
        showError(errorMessage);
        setIsLoading(false);
        return;
      } finally {
        setIsLoading(false);
      }
    }

    if (currentStep < maxSteps) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const getFieldsForStep = (step) => {
    const currentUserType = methods.watch('userType');

    switch (step) {
      case 1:
        return ['userType', 'accountType'];
      case 2:
        return ['fullName', 'phoneNumber', 'email', 'password'];
      case 3:
        if (currentUserType === 'buyer') {
          return ['location', 'emiratesIdNumber', 'eidExpiryDate'];
        }
        return ['location', 'vatNumber', 'emiratesIdNumber', 'idExpiryDate'];
      case 4:
        return ['idFront', 'idBack'];
      case 5:
        return currentUserType === 'seller'
          ? ['bankName', 'accountHolderName', 'accountNumber', 'iban']
          : [
            'businessName',
            'businessRegistrationNumber',
            'vatNumber',
            'tradeLicenseImage',
            'trnCertificationImage',
          ];
      case 6:
        return currentUserType === 'seller'
          ? [
            'businessName',
            'businessRegistrationNumber',
            'tradeLicenseImage',
            'trnCertificationImage',
          ]
          : [];
      default:
        return [];
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getAccountTypeId = (type) => {
    if (!type) return 1;
    return type.toLowerCase() === 'corporate' ? 2 : 1;
  };

  const onSubmit = async (data, event) => {
    if (event) event.preventDefault();
    console.log("[SignupForm] onSubmit triggered", { data, eventType: event?.type });

    // Safety check to prevent automatic submission if event is missing (though handleSubmit usually provides it)
    if (!event) {
      console.warn("onSubmit called without event check. Potential automatic trigger.");
    }

    setIsLoading(true);
    try {
      const accountTypeId = getAccountTypeId(data.accountType);
      const baseData = {
        name: data.fullName?.trim(),
        email: data.email?.trim().toLowerCase(),
        password: data.password,
        mobileNo: data.phoneNumber?.trim(),
        location: data.location?.trim(),
        userType: data.userType,
        accountType: data.accountType?.toLowerCase(),
      };

      let completeData = {};
      const files = {};

      if (data.userType === 'buyer') {
        completeData = {
          ...baseData,
          identificationNo: data.emiratesIdNumber?.trim(),
          idExprDate: data.eidExpiryDate,
          bankName: "",
          accountHolderName: "",
          buyerType: data.accountType?.toLowerCase(),
          type: data.accountType?.toLowerCase(),
          buyer_type: data.accountType?.toLowerCase(),
          userTypeId: accountTypeId,
          buyerTypeId: accountTypeId,
          buyer_type_id: accountTypeId,
          accountNumber: data.accountNumber?.trim() || "",
          iban: data.iban?.trim() || "",
        };

        completeData.businessName = data.businessName?.trim();
        completeData.businessRegNo = data.businessRegistrationNumber?.trim();
        completeData.vatNo = data.vatNumber?.trim();

        if (data.idFront?.[0]) files.eIdFrontImage = data.idFront[0];
        if (data.idBack?.[0]) files.eIdBackImage = data.idBack[0];

        if (data.tradeLicenseImage?.[0]) files.tradeLicenseImage = data.tradeLicenseImage[0];
        if (data.trnCertificationImage?.[0]) files.trnCertificateImage = data.trnCertificationImage[0];
      } else {
        completeData = {
          ...baseData,
          identificationNo: data.emiratesIdNumber?.trim(),
          idExprDate: data.idExpiryDate,
          vatNo: data.vatNumber?.trim() || "",
          bankName: data.bankName?.trim() || "",
          accountHolderName: data.accountHolderName?.trim() || "",
          accountNumber: data.accountNumber?.trim() || "",
          iban: data.iban?.trim() || "",
          sellerTypeId: accountTypeId,
        };

        completeData.businessName = data.businessName?.trim();
        completeData.businessRegNo = data.businessRegistrationNumber?.trim();

        if (data.idFront?.[0]) files.eIdFrontImage = data.idFront[0];
        if (data.idBack?.[0]) files.eIdBackImage = data.idBack[0];

        if (data.tradeLicenseImage?.[0]) files.tradeLicenseImage = data.tradeLicenseImage[0];
        if (data.trnCertificationImage?.[0]) files.trnCertificateImage = data.trnCertificationImage[0];
      }

      const formData = new FormData();
      Object.keys(completeData).forEach((key) => {
        if (completeData[key] !== null && completeData[key] !== undefined && completeData[key] !== "") {
          formData.append(key, completeData[key]);
        }
      });
      Object.keys(files).forEach((key) => {
        if (files[key]) {
          formData.append(key, files[key]);
        }
      });

      let response;
      if (data.userType === 'buyer') {
        response = await registerBuyer(formData);
      } else {
        response = await registerSeller(formData);
      }

      if (response && (response.data?.success || response.status === 200 || response.status === 201)) {
        // display server message when available (includes verify email instruction)
        const msg = response.data?.message || `${data.userType === 'buyer' ? 'Buyer' : 'Seller'} registration successful!`;
        showSuccess("Check your email for verification instructions");
        // after registration we don't auto-login; direct user to login page to sign in after verifying
        setTimeout(() => {
          navigate('/login');

        }, 2000);
      } else {
        throw new Error(response?.message || 'Registration failed');
      }
    } catch (error) {
      console.error("Registration error:", error);

      let errorMessage = "Registration failed. Please try again.";

      // Axios backend error
      if (error?.response?.data) {
        const backend = error.response.data;

        if (backend.error) {
          errorMessage = backend.error;
        } else if (backend.message) {
          errorMessage = backend.message;
        } else if (backend.errors) {
          if (Array.isArray(backend.errors)) {
            errorMessage = backend.errors.join(", ");
          } else {
            errorMessage = Object.values(backend.errors).join(", ");
          }
        }
      }

      // Fallback JS error
      else if (error.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
    }
    finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    const currentUserType = methods.watch('userType');
    switch (currentStep) {
      case 1:
        return <Step1UserAndAccountType onSelectUserType={handleUserTypeSelect} />;
      case 2:
        return <Step3AccountDetails />;
      case 3:
        return currentUserType === 'buyer'
          ? <BuyerStep4PersonalDetails />
          : <SellerStep4AdditionalDetails />;
      case 4:
        return currentUserType === 'buyer'
          ? <BuyerStep5Documents />
          : <SellerStep5Documents />;
      case 5:
        return currentUserType === 'buyer'
          ? <BuyerStep6BusinessDetails />
          : <SellerStep6BankDetails />;
      case 6:
        return <SellerStep7BusinessDetails />;
      default:
        return null;
    }
  };

  return (
    <main dir={isRTL ? "rtl" : "ltr"} lang={language} 
    style={{
      backgroundImage: `url(${bgimg})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      width: "100%",      
    }}
     className="min-h-screen min-w-3xl  flex items-center justify-center p-4">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit, (errors) => {
            const fieldsToTouch = getFieldsForStep(currentStep);
            fieldsToTouch.forEach(field => {
              methods.setValue(field, methods.getValues(field), { shouldTouch: true });
            });
            // Re-trigger validation for the current step to ensure all errors are definitely shown
            methods.trigger(fieldsToTouch);
            console.log('Form submission failed with errors:', errors);
          })}
          className="bg-bg-blue text-white rounded-xl w-full max-w-[650px] shadow-lg p-8 mx-auto transition-all duration-300"
        >
          <div className='text-center'>
            <h1 className="text-3xl font-bold  mb-4">
              {translate("auth.signup.getStarted")}
            </h1>
            <p className=" mb-6">
              {translate("auth.signup.fillDetails")}
            </p>
          </div>
          <div className="w-full overflow-x-auto sm:overflow-x-visible scrollbar-hide">
            <StepIndicator currentStep={currentStep} maxSteps={maxSteps} />
          </div>
          <div className="mt-8">{renderStep()}</div>
          <StepNavigation
            currentStep={currentStep}
            maxSteps={maxSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isLoading={isLoading}
          />
        </form>
      </FormProvider>
    </main >
  );
}

export default SignupForm;
